/**
 * <mf-scenes> – small helper for the scroll-snap carousel of sections/mf-scenes.liquid.
 * The carousel itself is native CSS (works without this file). This adds:
 *  - the slim progress bar (one segment button per card, shown only while the row really scrolls,
 *    aria-current on the segment of the visible card)
 *  - arrow keys / Home / End step card by card while the scroller has focus
 *  - the scroller is only a tab stop while it scrolls (not in the desktop grid)
 *  - theme editor: a selected block scrolls into view
 */
(() => {
  if (customElements.get('mf-scenes')) return;

  class MfScenes extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-mf-scenes-scroller]');
      this.cards = Array.from(this.querySelectorAll('[data-mf-scenes-card]'));
      this.nav = this.querySelector('[data-mf-scenes-dots]');
      this.dots = this.nav ? Array.from(this.nav.querySelectorAll('button')) : [];
      if (!this.scroller || !this.cards.length) return;

      this.index = 0;
      this.target = null;
      this.ratios = new Map();

      this.onKeydown = (event) => {
        if (event.target !== this.scroller || !this.scrollable) return;
        const last = this.cards.length - 1;
        const from = this.target === null ? this.index : this.target;
        const to = { ArrowRight: from + 1, ArrowLeft: from - 1, Home: 0, End: last }[event.key];
        if (to === undefined || event.altKey || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        this.goTo(to);
      };
      this.onDotClick = (event) => {
        const dot = event.target.closest('button');
        const to = this.dots.indexOf(dot);
        if (to > -1) this.goTo(to);
      };
      this.onBlockSelect = (event) => {
        const to = this.cards.indexOf(event.target);
        if (to > -1) this.goTo(to, false);
      };

      this.scroller.addEventListener('keydown', this.onKeydown);
      if (this.nav) this.nav.addEventListener('click', this.onDotClick);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if ('IntersectionObserver' in window) {
        // Two thresholds: "mostly visible" and "fully visible". The card with the highest ratio wins,
        // so the fully visible one beats a neighbour that still peeks in.
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));
            let best = this.index;
            let max = 0;
            this.cards.forEach((card, i) => {
              const ratio = this.ratios.get(card) || 0;
              if (ratio > max) {
                max = ratio;
                best = i;
              }
            });
            this.setCurrent(best);
          },
          { root: this.scroller, threshold: [0.5, 0.98] }
        );
        this.cards.forEach((card) => this.io.observe(card));
      }

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.sync());
        this.ro.observe(this.scroller);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      clearTimeout(this.timer);
      if (this.scroller) this.scroller.removeEventListener('keydown', this.onKeydown);
      if (this.nav) this.nav.removeEventListener('click', this.onDotClick);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* Does the row scroll at this width? Controls tab stop and dots. */
    sync() {
      this.scrollable = this.scroller.scrollWidth > this.scroller.clientWidth + 1;
      if (this.scrollable) this.scroller.setAttribute('tabindex', '0');
      else this.scroller.removeAttribute('tabindex');
      if (this.nav) this.nav.hidden = !this.scrollable || !this.io || this.cards.length < 2;
    }

    setCurrent(index) {
      this.index = index;
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goTo(index, smooth = true) {
      const to = Math.max(0, Math.min(index, this.cards.length - 1));
      const pad = parseFloat(getComputedStyle(this.scroller).scrollPaddingLeft) || 0;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Remember where we are heading so quick key presses add up while the scroll is still running
      this.target = to;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.target = null;
      }, 700);
      this.scroller.scrollTo({
        left: this.cards[to].offsetLeft - pad,
        behavior: smooth && !reduce ? 'smooth' : 'auto',
      });
    }
  }

  customElements.define('mf-scenes', MfScenes);
})();
